/**
 * Error Reporting Component
 * Centralized error handling and display with styling
 */

import React from 'react';
import { Alert } from './ui/Alert';
import { APP_CONFIG } from '../config';
import { logger } from '../utils';
import { useSafeThemeClasses } from '@/shared/theme';

export interface ErrorInfo {
  error: Error;
  errorInfo?: React.ErrorInfo;
  context?: string;
}

interface ErrorReportingProps {
  error: Error | string | null;
  title?: string;
  context?: string;
  showRetry?: boolean;
  showDetails?: boolean;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
}

/**
 * Main Error Reporting Component
 */
export const ErrorReporting: React.FC<ErrorReportingProps> = ({
  error,
  title = 'An error occurred',
  context,
  showRetry = false,
  showDetails = false,
  onRetry,
  onDismiss,
  className = '',
}) => {
  const theme = useSafeThemeClasses();
  const errorMessage = typeof error === 'string' ? error : error?.message || 'Unknown error';
  const errorStack = typeof error === 'string' ? null : error?.stack;

  // Log the error
  React.useEffect(() => {
    if (error) {
      logger.error('Error displayed to user', 'ErrorReporting', {
        message: errorMessage,
        context,
        stack: errorStack,
      });
    }
  }, [error, errorMessage, context, errorStack]);

  if (!error) return null;

  return (
    <div className={`error-reporting ${className}`}>
      <Alert
        type="error"
        title={title}
        message={errorMessage}
        dismissible={!!onDismiss}
        onDismiss={onDismiss}
      />
      
      {showDetails && errorStack && (
        <details className={`mt-4 p-4 ${theme.alertError} rounded-lg`}>
          <summary className={`cursor-pointer font-medium ${theme.textHeading}`}>
            Technical Details
          </summary>
          <pre className={`mt-2 text-xs ${theme.textSecondary} overflow-auto whitespace-pre-wrap`}>
            {errorStack}
          </pre>
        </details>
      )}

      {showRetry && onRetry && (
        <div className="mt-4 flex gap-2">
          <button
            onClick={onRetry}
            className={`px-3 py-1.5 text-sm rounded-lg transition-all ${theme.buttonSecondary} ${theme.buttonTextSecondary} border border-red-500/50`}
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Global Error Display Component
 * For app-level errors
 */
export const GlobalErrorDisplay: React.FC<ErrorInfo & { onReload?: () => void }> = ({
  error,
  context,
  onReload,
}) => {
  const theme = useSafeThemeClasses();
  
  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${theme.background}`}>
      <div className="max-w-md w-full">
        <div className={`rounded-xl shadow-lg p-8 text-center ${theme.card}`}>
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className={`text-2xl font-bold mb-4 ${theme.textHeading}`}>
            Application Error
          </h1>
          <p className={`mb-6 ${theme.textSecondary}`}>
            Something went wrong with {APP_CONFIG.name}. We've been notified and are working on a fix.
          </p>
          
          <ErrorReporting
            error={error}
            title="Error Details"
            context={context}
            showDetails={true}
            showRetry={true}
            onRetry={onReload || (() => window.location.reload())}
          />

          <div className={`mt-6 text-sm ${theme.textMuted}`}>
            Error ID: {Date.now().toString(36)}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Network Error Component
 */
export const NetworkError: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => {
  return (
    <ErrorReporting
      error="Unable to connect to the server. Please check your internet connection."
      title="Connection Error"
      context="network"
      showRetry={true}
      onRetry={onRetry}
      className="mb-4"
    />
  );
};

/**
 * Project ID Missing Error
 */
export const ProjectIdError: React.FC = () => {
  return (
    <ErrorReporting
      error="Project configuration is missing. Please contact support."
      title="Configuration Error"
      context="project_id"
      className="mb-4"
    />
  );
};

/**
 * Generic API Error Component
 */
export const ApiErrorDisplay: React.FC<{ 
  error: string | Error;
  operation?: string;
  onRetry?: () => void;
}> = ({ error, operation, onRetry }) => {
  const errorMessage = typeof error === 'string' ? error : error.message;
  
  return (
    <ErrorReporting
      error={errorMessage}
      title={operation ? `${operation} Failed` : 'Request Failed'}
      context="api"
      showRetry={!!onRetry}
      onRetry={onRetry}
      className="mb-4"
    />
  );
};

/**
 * Form Validation Error Component
 */
export const FormError: React.FC<{ 
  errors: Record<string, string>;
  title?: string;
}> = ({ errors, title = "Please fix the following errors:" }) => {
  const errorList = Object.entries(errors).map(([field, message]) => 
    `${field}: ${message}`
  ).join(', ');

  if (Object.keys(errors).length === 0) return null;

  return (
    <div className="form-error mb-4">
      <Alert
        type="error"
        title={title}
        message={errorList}
      />
    </div>
  );
};

/**
 * Error Boundary Fallback Component
 */
export const ErrorBoundaryFallback: React.FC<{
  error: Error;
  resetError: () => void;
}> = ({ error, resetError }) => {
  return (
    <GlobalErrorDisplay
      error={error}
      context="error_boundary"
      onReload={resetError}
    />
  );
};

export default ErrorReporting;
